import { Component, OnInit } from '@angular/core';
import { ColumnConfig, FilterConfig, JobPost } from '../../interfaces/job-post';
import { Router } from '@angular/router';
import { JobPostService } from './services/job-post.service';
import {
  PositionResponse,
  PositionService,
} from '../position/services/position.service';

@Component({
  selector: 'app-job-vacancy',
  templateUrl: './job-vacancy.component.html',
  styleUrls: ['./job-vacancy.component.css'],
})
export class JobVacancyComponent implements OnInit {
  constructor(
    private router: Router,
    private jobPostService: JobPostService,
    private positionService: PositionService,
  ) {}

  jobs: JobPost[] = [];
  positions: PositionResponse[] = [];

  // ── Server-side pagination state ──
  currentPage = 0; // 0-based for the API
  pageSize = 10;
  totalElements = 0;

  // ── Active server-side filters ──
  activeJobType = '';
  activePosition = '';
  activeWorkplace = '';
  searchQuery = '';
  searchTimeout: any = null;

  columns: ColumnConfig[] = [
    { label: 'Job Post Name', key: 'jobName' },
    { label: 'Position', key: 'position' },
    {
      label: 'Job Type',
      key: 'jobType',
      isBadge: true,
      badgeColorMap: {
        'Full-Time': 'badge-blue',
        'Part-Time': 'badge-purple',
        Freelance: 'badge-orange',
        'Project-Based': 'badge-pink',
      },
    },
    {
      label: 'Workplace',
      key: 'workplace',
      isBadge: true,
      badgeColorMap: {
        ONSITE: 'badge-green',
        REMOTE: 'badge-sky',
        HYBRID: 'badge-amber',
      },
    },
    {
      label: 'Status',
      key: 'jobStatus',
      isBadge: true,
      badgeColorMap: {
        DRAFT: 'badge-default',
        PUBLISHED: 'badge-green',
        CLOSED: 'badge-orange',
        ARCHIVED: 'badge-pink',
        PENDING_REVIEW: 'badge-amber',
        EXPIRED: 'badge-purple',
      },
    },
    // {
    //   label: 'Interview setup',
    //   key: 'interview',
    //   isButton: true,
    //   buttonLabel: 'Create interview',
    //   buttonClass: 'btn-create-interview',
    //   buttonDisabledKey: 'interviewId',
    // },
    // { label: 'Published Date', key: 'publishedDate' },
  ];

  filters: FilterConfig[] = [];
  searchKey = 'position';

  ngOnInit(): void {
    this.loadJobs(); // generateFilters is now called inside loadJobs
  }

  generateFilters(jobTypes: string[], positions: string[]): void {
    this.filters = [
      {
        label: 'Job Type',
        key: 'jobType',
        options: jobTypes,
      },
      {
        label: 'Position',
        key: 'position',
        options: positions,
      },
      {
        label: 'Workplace',
        key: 'workplace',
        options: ['ONSITE', 'REMOTE', 'HYBRID'],
      },
    ];
  }

  loadJobs(): void {
    const filters = {
      jobTypes: this.activeJobType || undefined,
      position: this.activePosition || undefined,
      workplaces: this.activeWorkplace || undefined,
    };

    const request$ = this.searchQuery.trim()
      ? this.jobPostService.searchJobPosts(
          this.searchQuery.trim(),
          this.currentPage,
          this.pageSize,
        )
      : this.jobPostService.getJobPosts(
          this.currentPage,
          this.pageSize,
          filters,
        );

    request$.subscribe({
      next: (res: any) => {
        const page = res.jobPosts;
        this.totalElements = page.totalElements;
        this.jobs = page.content.map((job: any) => ({
          id: job.id,
          interviewId:
            job.interviewId ?? job.interview_id ?? job.interview?.id ?? null,
          position: job.position?.title ?? job.positionTitle ?? '',
          jobType: job.jobType,
          workplace: job.workplace,
          jobStatus: job.jobStatus,
          jobName:
            job.jobPostTitle ||
            job.jobPostName ||
            job.position?.title ||
            job.positionTitle ||
            '',
          publishedDate: new Date(job.createdAt).toISOString().split('T')[0],
        }));
        this.generateFilters(res.jobTypes ?? [], res.positions ?? []);
      },
      error: (err) => console.error('Failed to load jobs:', err),
    });
  }

  onSearchChange(value: string): void {
    this.searchQuery = value;
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.currentPage = 0;
      this.loadJobs();
    }, 400);
  }

  // ── Called by the table's (pageChanged) output — receives 0-based page ──
  onPageChanged(page: number): void {
    this.currentPage = page;
    this.loadJobs();
  }

  onFilterChanged(filterKey: string, value: string): void {
    const normalized = value === 'All' ? '' : value;
    if (filterKey === 'jobType') this.activeJobType = normalized;
    if (filterKey === 'position') this.activePosition = normalized;
    if (filterKey === 'workplace') this.activeWorkplace = normalized;
    this.currentPage = 0; // reset to first page
    this.loadJobs();
  }

  onPostVacancy(): void {
    this.router.navigate(['/company-admin/add-job']);
  }

  onActionClicked(event: { action: string; item: any }): void {
    if (event.action === 'interview') {
      if (event.item.interviewId) {
        return;
      }
      this.router.navigate(['/company-admin/interview-setup'], {
        queryParams: {
          jobId: event.item.id,
          jobName: event.item.jobName,
          position: event.item.position,
        },
      });
    }
  }

  onEdit(job: JobPost): void {
    this.router.navigate(['/company-admin/edit-job', job.id]);
  }

  onView(job: JobPost): void {
    this.router.navigate(['/company-admin/view-job', job.id]);
  }
}
